import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components/macro";
import { mediaMin, sizes } from "styles/media";
import choresDesktopImgSrc from "./chores-desktop.png";
import choresMobileImgSrc from "./chores-mobile.png";
import choresTabletImgSrc from "./chores-tablet.png";
import choresImgSrc from "./chores.png";
import focusOnCoresDesktopImgSrc from "./focus-on-cores-desktop.png";
import focusOnCoresMobileImgSrc from "./focus-on-cores-mobile.png";
import focusOnCoresTabletImgSrc from "./focus-on-cores-tablet.png";
import focusOnCoresImgSrc from "./focus-on-cores.png";
import tooManyWorksDesktopImgSrc from "./too-many-works-desktop.png";
import tooManyWorksLineDesktopImgSrc from "./too-many-works-line-desktop.png";
import tooManyWorksLineMobileImgSrc from "./too-many-works-line-mobile.png";
import tooManyWorksLineTabletImgSrc from "./too-many-works-line-tablet.png";
import tooManyWorksLineImgSrc from "./too-many-works-line.png";
import tooManyWorksMobileImgSrc from "./too-many-works-mobile.png";
import tooManyWorksTabletImgSrc from "./too-many-works-tablet.png";
import tooManyWorksImgSrc from "./too-many-works.png";

const DESKTOP_SCROLL_MULTIPLES = 2.5; // 데스크탑 크기 이상에서 스크롤 배율
const TABLET_SCROLL_MULTIPLES = 1; // 데스크탑 크기 미만에서 스크롤 배율

const Wrapper = styled.section`
  width: 100%;
  height: 400vh;
  ${mediaMin.lg`
    height: ${DESKTOP_SCROLL_MULTIPLES * 300}vh;
  `}
`;

const TooManyWorksLineImg = styled.img``;

const TooManyWorksText = styled.div`
  position: absolute;
  top: calc(50% + 200px);
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: all 0.5s;
  white-space: nowrap;
  font-size: 18px;
  text-align: center;
  line-height: 1.4;
  font-weight: 400;

  ${mediaMin.xs`
    font-size: 20px;
    top: calc(50% + 200px);
  `}

  ${mediaMin.sm`
    font-size: 22px;
    top: calc(50% + 240px);
    br {
      display: none;
    }
  `}

  ${mediaMin.md`
    top: calc(50% + 280px);
  `}
`;

const StepTitle = styled.div`
  position: absolute;
  top: calc(50% - 208px);
  left: 0;
  font-size: 24px;
  font-weight: 500;
  opacity: 0;
  transition: opacity 0.5s;
  padding-left: 24px;

  ${mediaMin.xs`
    top: calc(50% - 240px);
    font-size: 32px;
  `}

  ${mediaMin.sm`
    top: calc(50% - 280px);
  `}

  ${mediaMin.md`
    top: calc(50% - 320px);
    font-size: 40px;
    padding-left: 0;
  `}

  ${mediaMin.lg`
    top: calc(50% - 280px);
    padding-left: 0;
  `}
`;

const FocusOnCoresTitle = styled(StepTitle)``;

const ChoresTitle = styled(StepTitle)``;

const TooManyWorksImg = styled.img``;

const FocusOnCoresImg = styled.img``;

const ChoresImg = styled.img``;

const Page = styled.div<{ step?: number }>`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100vh;
  position: sticky;
  top: 0;

  img {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: calc(100% - 32px);
    object-fit: contain;
    opacity: 0;
    transition: all 0.5s;

    ${mediaMin.md`
      width: 100%;
    `}
  }

  ${TooManyWorksLineImg} {
    opacity: 1;
  }

  ${({ step }) => {
    switch (step) {
      case 0:
        return css`
          ${TooManyWorksImg}, ${TooManyWorksText} {
            opacity: 1;
            transition-delay: 0.6s;
          }
        `;
      case 1:
        return css`
          ${TooManyWorksLineImg}, ${TooManyWorksImg}, ${TooManyWorksText} {
            opacity: 0;
            transition-delay: initial;
          }
          ${FocusOnCoresImg} {
            opacity: 1;
          }
          ${FocusOnCoresTitle} {
            opacity: 1;
            transition-delay: 0.5s;
          }
        `;
      case 2:
        return css`
          ${TooManyWorksLineImg}, ${TooManyWorksImg}, ${TooManyWorksText}, ${FocusOnCoresImg}, ${ChoresTitle} {
            opacity: 0;
            transition-delay: initial;
          }
          ${ChoresImg} {
            opacity: 1;
          }
          ${ChoresTitle} {
            opacity: 1;
            transition-delay: 0.5s;
          }
        `;
      case 3:
        return css`
          ${TooManyWorksLineImg}, ${TooManyWorksImg}, ${TooManyWorksText}, ${FocusOnCoresImg} {
            opacity: 0;
            transition-delay: initial;
          }
          ${ChoresImg} {
            transition-delay: 1s;
          }
          ${ChoresTitle} {
            opacity: 1;
          }
        `;
      default:
        return "";
    }
  }}
`;

const ImageWrapper = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
  width: 100%;

  ${mediaMin.xs`
    width: calc(100% - 80px);
  `}

  ${mediaMin.sm`
    width: calc(100% - 160px);
  `}

  ${mediaMin.md`
    width: calc(100% - 120px);
  `}

  ${mediaMin.lg`
    max-width: 1200px;
  `}
`;

export function ProblemSection() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(-1);

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.pageYOffset;
      if (wrapperRef.current) {
        const wrapperOffsetTop = wrapperRef.current.offsetTop;
        let multiples = DESKTOP_SCROLL_MULTIPLES;
        if (window.innerWidth < sizes.lg) {
          multiples = TABLET_SCROLL_MULTIPLES;
        }
        const scrollPoints = [
          wrapperOffsetTop,
          wrapperOffsetTop + window.innerHeight * multiples,
          wrapperOffsetTop + window.innerHeight * multiples * 2,
          wrapperOffsetTop + window.innerHeight * multiples * 3,
        ];

        if (currentScroll < scrollPoints[0]) {
          setStep(-1);
        } else if (currentScroll < scrollPoints[1]) {
          setStep(0);
        } else if (currentScroll < scrollPoints[2]) {
          setStep(1);
        } else if (currentScroll < scrollPoints[3]) {
          setStep(2);
        } else {
          setStep(3);
        }
      }
    };
    document.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <Wrapper ref={wrapperRef}>
      <Page step={step}>
        <ImageWrapper>
          <picture>
            <source
              media={`(min-width:${sizes.lg}px)`}
              srcSet={tooManyWorksLineImgSrc}
            />
            <source
              media={`(min-width:${sizes.md}px)`}
              srcSet={tooManyWorksLineDesktopImgSrc}
            />
            <source
              media={`(min-width:${sizes.xs}px)`}
              srcSet={tooManyWorksLineTabletImgSrc}
            />
            <TooManyWorksLineImg src={tooManyWorksLineMobileImgSrc} />
          </picture>
          <picture>
            <source
              media={`(min-width:${sizes.lg}px)`}
              srcSet={tooManyWorksImgSrc}
            />
            <source
              media={`(min-width:${sizes.md}px)`}
              srcSet={tooManyWorksDesktopImgSrc}
            />
            <source
              media={`(min-width:${sizes.xs}px)`}
              srcSet={tooManyWorksTabletImgSrc}
            />
            <TooManyWorksImg src={tooManyWorksMobileImgSrc} />
          </picture>
          <TooManyWorksText>
            Damn right. <br />
            There are too many works for work.
          </TooManyWorksText>
          <picture>
            <source
              media={`(min-width:${sizes.lg}px)`}
              srcSet={focusOnCoresImgSrc}
            />
            <source
              media={`(min-width:${sizes.md}px)`}
              srcSet={focusOnCoresDesktopImgSrc}
            />
            <source
              media={`(min-width:${sizes.xs}px)`}
              srcSet={focusOnCoresTabletImgSrc}
            />
            <FocusOnCoresImg src={focusOnCoresMobileImgSrc} />
          </picture>
          <FocusOnCoresTitle>You focus on the cores</FocusOnCoresTitle>
          <picture>
            <source media={`(min-width:${sizes.lg}px)`} srcSet={choresImgSrc} />
            <source
              media={`(min-width:${sizes.md}px)`}
              srcSet={choresDesktopImgSrc}
            />
            <source
              media={`(min-width:${sizes.xs}px)`}
              srcSet={choresTabletImgSrc}
            />
            <ChoresImg src={choresMobileImgSrc} />
          </picture>
          <ChoresTitle>We take the chores</ChoresTitle>
        </ImageWrapper>
      </Page>
    </Wrapper>
  );
}
